import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useUserPreferences, useResponsive } from '@apparatix/composables';

export const useSidebarStore = defineStore('sidebar', () => {
  const { getPreferences } = useUserPreferences();
  const { isMobile } = useResponsive();

  // 'static' is used for Stay Open, 'dynamic' is used for Hover to Open. On mobile type is set to dynamic regardless of saved user preference
  const type = ref<'static' | 'dynamic'>('static');

  // Used to completely hide the sidebar, regardless of current type
  const shouldHide = ref(false);

  // Used to control whether the sidebar is currently open
  // - when type is dynamic: controls whether the flyout is visible
  // - when type is static: controls whether the sidebar is expanded or collapsed
  const isOpen = ref(true);

  // Used to set whether the flyout should automatically close when an item is clicked. Currently only used on mobile
  const closeOnItemClick = ref(false);

  const setup = async () => {
    if (isMobile) {
      setDynamicMode();
      closeOnItemClick.value = true;
      return;
    }

    const preferences = await getPreferences();
    if (preferences?.collapseSidebar) {
      setDynamicMode();
    } else {
      setStaticMode();
    }
  };

  const toggleOpen = () => isOpen.value = !isOpen.value;

  const setDynamicMode = () => {
    type.value = 'dynamic';
    isOpen.value = false;
  };

  const setStaticMode = () => {
    type.value = 'static';
    isOpen.value = true;
  };

  setup();

  return {
    type,
    shouldHide,
    isOpen,
    toggleOpen,
    closeOnItemClick,
  };
});
